import { ERROR_CODES } from '@constants'
import { useDialog } from '@dni-hooks/useDialog'
import { type ResponseErrorGenerics } from '@dni-types/utilities/response'
import { useApiCancelPaymentTransaction } from '@hooks-api/fortune/useApiCancelPaymentTransaction'
import { Button } from '@pets-ui/Button/Button'
import { Dialog } from '@pets-ui/Dialog/Dialog'
import { DialogActions } from '@pets-ui/DialogActions/DialogActions'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { ErrorFilled, SuccessFilled } from '@pets-ui/icons'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { type AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom-v5'

export const DIALOG_CONFIRM_CANCEL_TRANSACTION = 'DIALOG_CONFIRM_CANCEL_TRANSACTION'
export interface DialogConfirmCancelTransactionProps {
  transactionId: string
  onCancelOrderClick?: () => void
  onCancelSuccess?: () => void
  basePath: string
}

interface DialogConfirmCancelTransactionContextProps {
  closeDialog: () => void
  transactionId: string
  onCancelOrderClick?: () => void
  onCancelSuccess?: () => void
  basePath: string
}

const DialogConfirmCancelTransactionContext = ({
  closeDialog,
  transactionId,
  onCancelOrderClick,
  onCancelSuccess,
  basePath,
}: DialogConfirmCancelTransactionContextProps) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { trigger: triggerCancelOrder } = useApiCancelPaymentTransaction()
  const { openSnackbar } = useSnackbar()
  const handleClickConfirm = async () => {
    closeDialog()
    onCancelOrderClick?.()
    try {
      await triggerCancelOrder({ id: transactionId })
      onCancelSuccess?.()
      openSnackbar({
        message: t('orderDetail.orderCancelled', { defaultValue: '訂單已取消' }),
        offsets: {
          top: '100px',
        },
        anchorOrigin: { horizontal: 'center', vertical: 'top' },
        icon: <SuccessFilled color={colors.green600} />,
      })
      history.push(`${basePath}/my-account/order?tabValue=cancelled&focusedTransactionId=${transactionId}`)
    }
    catch (error) {
      const errorData = (error as AxiosError<{ error: ResponseErrorGenerics }>).response?.data.error
      const message = errorData?.code === ERROR_CODES.ATM_CVS_PAYME_PAYMENT_ORDER_CANCEL
        ? t('orderDetail.cancelOrderFailedForReadyForShipment', { defaultValue: '訂單取消失敗，商品準備出貨，無法自行取消' })
        : t('orderDetail.cancelOrderFailed', { defaultValue: '取消訂單失敗' })

      openSnackbar({
        message,
        offsets: {
          top: '100px',
        },
        anchorOrigin: { horizontal: 'center', vertical: 'top' },
        icon: <ErrorFilled color={colors.red600} />,
      })
      console.error(`[Cancel transaction] ${error}`)
    }
  }
  const handleCancel = () => {
    closeDialog()
  }
  return (
    <>
      <DialogHeader>{t('orderDetail.cancelOrder', { defaultValue: '取消訂單' })}</DialogHeader>
      <DialogContent>
        <Text align="center">{t('orderDetail.cancelOrderConfirmMessage', { transactionId, defaultValue: `取消交易編號 ${transactionId} 後，將無法恢復訂單，是否繼續取消？` })}</Text>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} fullWidth variant="outlined" color="grey">{t('orderDetail.discard', { defaultValue: '捨棄' })}</Button>
        <Button onClick={handleClickConfirm} fullWidth>{t('orderDetail.confirm', { defaultValue: '確認' })}</Button>
      </DialogActions>
    </>
  )
}

export function DialogConfirmCancelTransaction({ onCancelOrderClick }: { onCancelOrderClick?: () => void }) {
  const { open, closeDialog, transactionId, basePath, onCancelSuccess } = useDialog<DialogConfirmCancelTransactionProps>(DIALOG_CONFIRM_CANCEL_TRANSACTION)

  return (
    <>
      <Dialog disablePortal open={open} maxWidth={320}>
        <DialogConfirmCancelTransactionContext closeDialog={closeDialog} transactionId={transactionId} onCancelOrderClick={onCancelOrderClick} onCancelSuccess={onCancelSuccess} basePath={basePath} />
      </Dialog>
    </>
  )
}
